import { useState, useEffect } from 'react'
import { apiUrl, authFetch } from '../../lib/api'

interface Template { id: string; name: string; icon: string; cmd: string; desc: string }

export function TemplatesPanel({ onCreated }: { onCreated?: () => void }) {
  const [templates, setTemplates] = useState<Template[]>([])
  const [creating, setCreating] = useState<string | null>(null)
  const [projectName, setProjectName] = useState('my-project')
  const [status, setStatus] = useState('')

  useEffect(() => {
    authFetch(apiUrl('/api/tools/templates')).then((r) => r.json()).then(setTemplates).catch(() => {})
  }, [])

  const create = async (t: Template) => {
    if (!projectName.trim()) return
    setCreating(t.id)
    setStatus(`Creating ${t.name}...`)
    const res = await authFetch(apiUrl('/api/tools/templates/create'), {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cmd: t.cmd, name: projectName.trim() }),
    })
    const data = await res.json()
    setCreating(null)
    setStatus(data.ok ? `Created ${projectName}!` : data.error ?? 'Failed')
    if (data.ok) {
      window.dispatchEvent(new CustomEvent('pulse:files-changed'))
      onCreated?.()
    }
    setTimeout(() => setStatus(''), 4000)
  }

  return (
    <div style={panelStyle}>
      <div style={headerStyle}><span>NEW PROJECT</span></div>

      <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        <div style={{ fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginBottom: 6 }}>Project name</div>
        <input value={projectName} onChange={(e) => setProjectName(e.target.value.replace(/[^a-zA-Z0-9-_]/g, ''))} style={inputStyle} placeholder="my-project" />
        {status && <div style={{ fontSize: 10, color: 'var(--accent)', fontFamily: 'var(--font-mono)', marginTop: 6 }}>{status}</div>}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
        {templates.map((t) => (
          <div key={t.id} onClick={() => !creating && create(t)} style={{
            padding: '10px 14px', cursor: creating ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', gap: 10,
            borderBottom: '1px solid var(--border)', transition: 'background 0.08s',
            opacity: creating && creating !== t.id ? 0.4 : 1,
            background: creating === t.id ? 'rgba(124,110,245,0.08)' : 'none',
          }}>
            <span style={{ fontSize: 20 }}>{t.icon}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-0)', fontFamily: 'var(--font-mono)' }}>{t.name}</div>
              <div style={{ fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>{t.desc}</div>
            </div>
            {creating === t.id && <span style={{ fontSize: 10, color: 'var(--accent)' }}>creating...</span>}
          </div>
        ))}
      </div>
    </div>
  )
}

const panelStyle: React.CSSProperties = { width: '100%', height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg-1)', borderRight: '1px solid var(--border)', overflow: 'hidden' }
const headerStyle: React.CSSProperties = { padding: '8px 14px', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--text-2)', textTransform: 'uppercase', borderBottom: '1px solid var(--border)', flexShrink: 0 }
const inputStyle: React.CSSProperties = { width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border-2)', background: 'var(--bg-2)', color: 'var(--text-0)', fontFamily: 'var(--font-mono)', fontSize: 12, outline: 'none' }
